import type { ElementOf } from "ts-essentials";

import type { FromModel, ModelDefinition } from "@/__main__/data-model.mjs";
import createModel, {
  Mapped,
  Optional,
  Tuple,
  Union,
} from "@/__main__/data-model.mjs";
import isObject from "@/util/is-object.mjs";

const item = {
  Name: Optional(String),
  ShortName: Optional(String),
  Description: Optional(String),
  FullName: Optional(String),
  FirstName: Optional(String),
  Nickname: Optional(String),
  Location: Optional(String),
  name: Optional(String),
  description: Optional(String),
  successMessageText: Optional(String),
  failMessageText: Optional(String),
  acceptPlayerMessage: Optional(String),
  declinePlayerMessage: Optional(String),
  completePlayerMessage: Optional(String),
} satisfies ModelDefinition;

const RegExpQuoteObj = /\b\d\b/; // Single digit numerical value as a string

export const ItemsLocaleValidate = createModel(
  Mapped({
    key: String,
    value: Union([item, Mapped({ key: String, value: String }), String]),
  }),
).transform(
  Mapped({
    key: String,
    value: item,
  }),
  (response) => {
    return Object.entries(response).reduce<
      Record<string, FromModel<typeof item>>
    >((acc, [key, value]) => {
      if (isObject(value)) {
        // These particular objects are just quotes in single digit key string hashes
        for (const i in value) {
          if (RegExpQuoteObj.test(i)) {
            continue;
          }

          // Some objects have keys with empty string values
          if (Object.values(value).every((j) => !j)) {
            continue;
          }

          acc[key] = value;
        }
      }

      return acc;
    }, {});
  },
);

const mapTypes = [
  "pmcExtract",
  "scavExtract",
  "sharedExtract",
  "transitExtract",
] as const;

const mapMarker = {
  /** The Leaflet X, Y coordinates for the marker. */
  loc: Tuple([Number, Number]),
  /** The type of marker to render */
  type: Union(mapTypes),
  /** The primary name of the marker */
  name: String,
  /** Any requirements that are necessary for the marker to become active/be completed */
  requirements: Optional(String),
  /** Whether the marker should be disabled for the current version */
  disabled: Optional(Boolean),
  /** Whether this exfil is secret. */
  secret: Optional(Boolean),
} satisfies ModelDefinition;

export const MapMarkerValidate = createModel([mapMarker]);

export type ItemsLocale = FromModel<typeof ItemsLocaleValidate>;

export type ItemLocale = ItemsLocale[string];

export type MapMarkers = FromModel<typeof MapMarkerValidate>;

export type MapMarker = ElementOf<MapMarkers>;
